import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import AuroraTargetTemperatureInput from './AuroraTargetTemperatureInput'
import { formatTemperature } from '../utils/string'
import AuroraControl from './AuroraControl'

const MAX_TARGET_TEMPERATURE = 100

const MIN_TARGET_TEMPERATURE = 0
interface AuroraTemperatureControlProps {
  value: number | null
  loading?: boolean
  disabled?: boolean
  min?: number | null
  max?: number | null
  onSubmit: (value: number) => Promise<void>
}

export default function AuroraTargetTemperatureControl({
  value: targetTemperature,
  loading = false,
  disabled = false,
  min = null,
  max = null,
  onSubmit
}: AuroraTemperatureControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The new target temperature.
   */
  const [newTargetTemperature, setNewTargetTemperature] = useState<number | null>(null)

  const clampCeil = (value: number) => {
    const clamped = Math.min(value, max ?? MAX_TARGET_TEMPERATURE)

    return Math.max(clamped, min ?? MIN_TARGET_TEMPERATURE)
  }

  const clampFloor = (value: number) => {
    const clamped = Math.max(value, min ?? MIN_TARGET_TEMPERATURE)

    return Math.min(clamped, max ?? MAX_TARGET_TEMPERATURE)
  }

  /**
   * Increments the target temperature by 0.5 degrees.
   */
  function handleIncrement() {
    setNewTargetTemperature((prev) => {
      const value = prev !== null ? prev : targetTemperature

      if (value === null) {
        return null
      }

      return clampCeil(Math.ceil(value * 2) / 2 + 0.5)
    })
  }

  /**
   * Decrements the target temperature by 0.5 degrees.
   */
  function handleDecrement() {
    setNewTargetTemperature((prev) => {
      const value = prev !== null ? prev : targetTemperature

      if (value === null) {
        return null
      }

      return clampFloor(Math.floor(value * 2) / 2 - 0.5)
    })
  }

  /**
   * Update the target temperature.
   */
  async function handleConfirm() {
    if (newTargetTemperature === null) {
      return
    }

    try {
      await onSubmit(newTargetTemperature)

      setNewTargetTemperature(null)
    } catch {
      // Ignore the error.
    }
  }

  /**
   * Reset the new target temperature.
   */
  function handleReset() {
    setNewTargetTemperature(null)
  }

  return (
    <AuroraControl
      direction="column"
      label={t('targetTemperature')}
      loading={loading}
      disabled={disabled}
      control={
        <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
          <Stack direction="row" alignItems="center" gap={3}>
            {targetTemperature !== null && (
              <IconButton
                color={newTargetTemperature !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{ border: '1px solid' }}
                onClick={handleDecrement}
              >
                <Remove />
              </IconButton>
            )}

            <Stack direction="column" justifyContent="center" alignItems="center" gap={1}>
              {newTargetTemperature !== null ? (
                <AuroraTargetTemperatureInput
                  value={newTargetTemperature}
                  loading={loading}
                  onChange={setNewTargetTemperature}
                  onSubmit={handleConfirm}
                />
              ) : targetTemperature !== null ? (
                <Typography variant="h2" color="text.primary">
                  {formatTemperature(targetTemperature)}
                </Typography>
              ) : (
                <Skeleton variant="text" width={80} height={48} />
              )}
            </Stack>

            {targetTemperature !== null && (
              <IconButton
                color={newTargetTemperature !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{
                  border: '2px solid'
                }}
                onClick={handleIncrement}
              >
                <Add />
              </IconButton>
            )}
          </Stack>

          {newTargetTemperature !== null && (
            <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
              <LoadingButton
                variant="outlined"
                color="primary"
                size="small"
                loading={loading}
                disabled={disabled}
                onClick={handleConfirm}
              >
                {t('actions:confirm')}
              </LoadingButton>

              <Button
                variant="text"
                color="inherit"
                size="small"
                disabled={loading || disabled}
                onClick={handleReset}
              >
                {t('actions:cancel')}
              </Button>
            </Stack>
          )}
        </Stack>
      }
    />
  )
}
